import { useEffect, useRef } from 'react';
import { socketManager } from 'utils/socketManager';
import { PLAY_SOCKET_EVENTS } from 'constants/play';
import type { PlayNotifyPayload } from 'types/interfaces/play';
import { showPlayNotifyToast } from 'components/PlayNotifyToast/PlayNotifyToast';

interface Options {
  enabled?: boolean;
}

/**
 * TradeMagic Play in-app banners (Asana 1220000146037011, Decisions 13, 19, 20).
 * play:notify arrives on the user's own socket room for ROUND_START, JUDGE_CALL,
 * RESULT_DISPUTED and OPPONENT_DROPPED, with the backend's title and body.
 * Mounted in SocketProvider like the other user-room hooks, so a player parked
 * on /home still gets "Round N has started". Room events never raise toasts.
 */
export const usePlayNotifySocket = ({ enabled = true }: Options = {}): void => {
  const acquired = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const socket = socketManager.acquireSocket();
    acquired.current = true;

    const onNotify = (payload: PlayNotifyPayload) => {
      // socket.io fires synchronously; keep toast rendering off the reducer path.
      queueMicrotask(() => showPlayNotifyToast(payload));
    };
    socket.on(PLAY_SOCKET_EVENTS.notify, onNotify);

    return () => {
      socket.off(PLAY_SOCKET_EVENTS.notify, onNotify);
      if (acquired.current) {
        socketManager.releaseSocket();
        acquired.current = false;
      }
    };
  }, [enabled]);
};

export default usePlayNotifySocket;
